import { useContext, useEffect, useMemo } from "react";
import { ProductList } from "../ProductList";
import { ContentContainer } from "./BrandView.styled";
import { PLPType } from "src/types/PLPType.d";
import algoliasearch from "algoliasearch";
import {
  BRAND_FACET,
  CATEGORIES_FACET,
  CONSCIOUS_FACET,
} from "src/utils/algolia";
import { InstantSearchServerState } from "react-instantsearch-core-v7";
import { HeaderContainer } from "../ProductCategoryView/ProductCategoryView.styled";
import { AlgoliaSearchBrand, Gender } from "src/types/Algolia";
import { Header } from "./components/Header";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useDispatch, useSelector } from "react-redux";
import { getGenderId, setGender } from "src/modules/page";
import { PopularBrandsWrapper } from "../PopularBrands";
import { BrandSEO } from "src/molecules/BrandSEO";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import { ShopType } from "src/types/graphql.d";
import getAllBrandsPageRoute from "src/utils/getAllBrandsPageRoute";
import { Locale } from "lib/Intl";
import { useBreakpoints } from "@otrium/core";
import { AlgoliaIconicProducts } from "src/modules/brands/components/AlgoliaIconicProducts";
import { PageHead } from "src/molecules/PageHead";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import getDeepLink from "src/utils/getDeepLink";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { useRouter } from "next/router";
import { AppCtx } from "src/contexts/app.context";

type BrandViewProps = {
  gender: string;
  slug: string;
  title?: string;
  indexName: string;
  locale: string;
  categoryPageId: string;
  search: {
    appId: string;
    searchKey: string;
  };
  brand: AlgoliaSearchBrand;
  serverState?: InstantSearchServerState;
};

function BrandView({
  categoryPageId,
  gender,
  indexName,
  search,
  slug,
  title,
  brand,
  locale,
  serverState,
}: BrandViewProps): JSX.Element {
  const { setPageViewedProperties } = useContext(AppCtx);
  const { i18n } = useLingui();
  const router = useRouter();

  const dispatch = useDispatch();
  const currentGender = useSelector(getGenderId);
  const searchClient = useMemo(
    () => algoliasearch(search?.appId, search?.searchKey),
    [search?.appId, search?.searchKey]
  );
  const { isDesktop } = useBreakpoints();

  const genderId = useMemo(() => localeFix({ gender }), [gender]);

  const viewAllink = useMemo(
    () =>
      getAllBrandsPageRoute({
        shopType: genderId as ShopType,
        locale: locale as Locale,
      }),
    [genderId, locale]
  );

  useEffect(() => {
    if (currentGender !== genderId) {
      dispatch(setGender(genderId));
    }
    if (brand) {
      setPageViewedProperties({
        page_type: "brand",
        brand: brand?.name,
        brand_id: brand.objectID,
      });
    }
  }, [genderId, currentGender, brand, dispatch, setPageViewedProperties]);

  return (
    <>
      <PageHead
        title={t(i18n)`${
          brand?.name ? `${brand?.name} | ` : ""
        }Online Outlet | Otrium`}
        deeplink={getDeepLink({
          hostname: `${getI18nRoute({
            route: `/brands`,
            locale: locale as Locale,
          })}/${slug}/${gender}`,
          query: {
            cat_slug: router.query?.cat_slug,
            discount: router.query?.discount,
          },
        })}
        description={brand?.description || ""}
        enableIndexation
      />
      <ContentContainer>
        <HeaderContainer>
          <Header
            brand={brand}
            genderId={localeFix({ gender }) as Gender}
            slug={slug}
          />

          <AlgoliaIconicProducts
            genderId={gender}
            localeGenderId={genderId}
            slug={slug}
            plpType={PLPType.BRAND_PLP}
            noPadding
          />
        </HeaderContainer>

        <ProductList
          title={title}
          categoryPageId={categoryPageId}
          gender={gender}
          indexName={indexName}
          pageContentType={PLPType.BRAND_PLP}
          searchClient={searchClient}
          slug={slug}
          excludeFilters={
            isDesktop
              ? [CATEGORIES_FACET, BRAND_FACET, CONSCIOUS_FACET]
              : [BRAND_FACET, CONSCIOUS_FACET]
          }
          serverState={serverState}
        />

        <PopularBrandsWrapper
          genderId={gender}
          localeGenderId={genderId}
          excludeSlug={slug}
          viewAllLink={viewAllink}
          plpType={PLPType.BRAND_PLP}
        />

        <BrandSEO brandSlug={slug} shopType={genderId as ShopType} />
      </ContentContainer>

      <RRPFootnote />
    </>
  );
}

export { BrandView };
